import { useEffect, useRef, useState } from "react";
// Redux
import { useSelector } from "react-redux";
// React Query
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
// MUI
import { Grid, Skeleton, Stack, Typography } from "@mui/material";

// Context
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";

// Hooks
import { useAuthorization } from "@Common/hooks/Authorization/Authorization";
import { useManagedPositions } from "@Common/hooks/Positions";

// API
import { getDraws, getOverdraws, ingestDraws } from "@DataPages/api/draw";
import actionsList from "@Constants/actionsList";

// Components
import EmptyBody from "@Layouts/components/Basics/EmptyBody";
import CountDownButton from "@Layouts/components/Buttons/CountDownButton";
import TextIconMessage from "@Layouts/components/Basics/TextIconMessage";
import DrawsContent from "./DrawsContent";

// Helpers
import { and, or, ternaryResolver } from "@Common/helpers/sonar";
import { isStatusCompleted } from "@Common/helpers/PayPeriods";

// Assets
import empty from "@/assets/images/commons/empty.png";
import emptyTable from "@/assets/images/commons/empty_table.png";

// Helpers
import { getLocalDate } from "@/Common/helpers/Dates";

// Types
import { RootState } from "@/redux/store";
import { IPeriodTreeLeaf } from "@Layouts/components/SideBars/PayPeriodSideBar/interfaces";
import { Nullable } from "@Common/interfaces/types.ts";
import { GraphqlSubscriptionResponse } from "@Common/api/types/GraphqlSubscriptionResponse";
import { IDraw } from "@DataPages/types/draw/models.ts";

// Constants
import { DATE_FORMAT } from "@/Constants/date";

/********************************************* HELPERS ************************************************/
const getLastUpdatedDate = (date: Date | string, timezone: string | undefined, dateFormat = DATE_FORMAT.usDateTime12hUnpadded) => {
	if (date) {
		return getLocalDate(date, dateFormat, timezone) ?? "";
	}
	return null;
};

const displayEmptyBody = ({
	hasData,
	inLoadingState,
	selectedPayPeriod,
	emptyImg,
	emptyTableImg,
}: {
	hasData: boolean;
	inLoadingState: boolean;
	selectedPayPeriod: Nullable<IPeriodTreeLeaf>;
	emptyImg: string;
	emptyTableImg: string;
}) => {
	if (hasData || inLoadingState) {
		return null;
	}

	return (
		<EmptyBody
			image={selectedPayPeriod ? emptyTableImg : emptyImg}
			title={selectedPayPeriod ? "No Draws and Overdraws found for this month" : "Select a month to view their Draws and Overdraws"}
			bodyHeight={"480px"}
			sx={{
				boxShadow: "none !important",
			}}
		/>
	);
};

type DrawBodyProps = Readonly<{
	selectedPayPeriod: Nullable<IPeriodTreeLeaf>;
	isUpdatable: boolean;
}>;
export default function DrawBody(props: DrawBodyProps) {
	const { selectedPayPeriod } = props;

	const {
		payPeriodId: selectedPayPeriodId = "",
		cycleId: commissionCycleIdOfSelectedPP = "",
		status: payPeriodStatus = "",
	} = selectedPayPeriod ?? {};

	const subscriptionId = useRef<string | null>(null);
	const [ingesting, setIngesting] = useState(false);

	const location = useSelector((state: RootState) => state.location.location);
	const locationId = location?.locationId ?? "";
	const queryClient = useQueryClient();

	// Permissions
	const { useAuth } = useAuthorization();
	const SYNC_DRAW_AMOUNTS = useAuth(actionsList.SYNC_DRAW_AMOUNTS);

	/********************************************* QUERIES ************************************************/
	const { data: managedPositions, isLoading: isLoadingPositions } = useManagedPositions({
		queryOpts: {
			staleTime: Infinity,
		},
	});

	const isEnabled = and(Boolean(managedPositions), Boolean(locationId), Boolean(selectedPayPeriod));
	const [{ data: draws, isLoading: isLoadingDraws }, { data: overdraws, isLoading: isLoadingOverdraws, refetch }] = useQueries({
		queries: [
			{
				queryKey: ["draws", locationId, commissionCycleIdOfSelectedPP, selectedPayPeriodId],
				queryFn: () => getDraws(locationId, commissionCycleIdOfSelectedPP, selectedPayPeriodId),
				enabled: isEnabled,
				staleTime: Infinity,
			},
			{
				queryKey: ["overdraws", locationId, commissionCycleIdOfSelectedPP, selectedPayPeriodId],
				queryFn: () => getOverdraws(locationId, commissionCycleIdOfSelectedPP, selectedPayPeriodId),
				enabled: isEnabled,
				staleTime: Infinity,
			},
		],
	});

	/********************************************* MUTATIONS ************************************************/
	const { mutate: ingestData } = useMutation({
		mutationFn: async () => {
			setIngesting(true);
			await ingestDraws(locationId, commissionCycleIdOfSelectedPP, selectedPayPeriodId);
			refetch();
			return true;
		},
	});

	/********************************************* SUBSCRIPTIONS ************************************************/
	const { getSubscription } = useSubscriptions();
	const drawSubscription = getSubscription(["draws", commissionCycleIdOfSelectedPP, selectedPayPeriodId]);
	drawSubscription?.subscriptions?.onCreateDraws?.setCallback((result: GraphqlSubscriptionResponse<"onCreateDraws", IDraw>) => {
		setIngesting(false);
		const updatedDraws = result.data?.onCreateDraws;

		if (updatedDraws) {
			queryClient.setQueryData(["draws", locationId, commissionCycleIdOfSelectedPP, selectedPayPeriodId], () => updatedDraws);
		}
	});

	const isLoadingSomething = or(isLoadingPositions, isLoadingDraws, isLoadingOverdraws);
	const hasData = or(Boolean(draws?.draws?.length), Boolean(overdraws?.overdraws?.length));
	const inLoadingState = or(isLoadingSomething, ingesting);

	const displayCountDownButton = () => {
		if (!props.isUpdatable) {
			return null;
		}

		const statusCondition = isStatusCompleted(payPeriodStatus);

		return (
			<CountDownButton
				disabled={or(!SYNC_DRAW_AMOUNTS, !statusCondition)}
				typographySx={{ fontSize: "12px" }}
				initialCopy={"Update Now"}
				actionCopy={"Updating..."}
				loading={isLoadingSomething}
				counter={60}
				onClickCallBack={() => ingestData()}
			/>
		);
	};

	const isMarginRight = or(isLoadingSomething, Boolean(draws), Boolean(overdraws));

	useEffect(() => {
		if (props.selectedPayPeriod) {
			subscriptionId.current = props.selectedPayPeriod._id;
		}

		return () => {
			subscriptionId.current = null;
			setIngesting(false);
		};
	}, [props.selectedPayPeriod]);

	return (
		<Grid container alignContent={"flex-start"} sx={{ height: "100%" }}>
			<Grid item xs={12}>
				{isLoadingSomething ? (
					<Skeleton variant="rectangular" height={25} width={350} sx={{ borderRadius: "6px" }} />
				) : (
					<>
						{props.selectedPayPeriod && (
							<Typography
								className={"page-title"}
								sx={{ lineHeight: "1.2" }}
							>{`${props.selectedPayPeriod?.title} Draws and Overdraws`}</Typography>
						)}
					</>
				)}
			</Grid>

			<Grid container item alignContent={"space-between"} sx={{ height: "100%" }}>
				<Grid item xs={12}>
					<DrawsContent
						draws={draws?.draws ?? []}
						overdraws={overdraws?.overdraws ?? []}
						positions={managedPositions ?? []}
						loading={isLoadingSomething || ingesting}
					/>
				</Grid>

				{displayEmptyBody({
					emptyImg: empty,
					emptyTableImg: emptyTable,
					selectedPayPeriod,
					hasData,
					inLoadingState,
				})}

				<Grid item xs={12} className="!mb-[32px]" mt={!hasData ? "-35px" : "0px"}>
					<Stack direction={"row"} alignItems={"center"}>
						<TextIconMessage
							icon
							title={"Last Update"}
							content={getLastUpdatedDate(draws?.updatedAt ?? "", location?.timezone ?? "")}
							showContent={Boolean(draws)}
							loading={isLoadingSomething}
							sx={{
								marginRight: ternaryResolver(isMarginRight, "10px", "0px"),
								color: "var(--text-color)",
							}}
						/>

						{displayCountDownButton()}
					</Stack>
				</Grid>
			</Grid>
		</Grid>
	);
}
